import { Component, OnInit, ElementRef, EventEmitter, ViewChild } from '@angular/core';
import { IAlert } from './common/i-alert';
import {HttpErrorResponse} from '@angular/common/http';

@Component({
  standalone: false,
  selector: 'app-sms-abstract-app-page',
  template: ''
})
export abstract class SmsAbstractAppPage {

    @ViewChild('error_alert')  myScrollContainer: ElementRef;

  	public alerts: Array<IAlert> = [];
  	error_count: number = 0;

  /**
   * A request that never completes - a timeout, a dropped connection, a failed
   * CORS preflight - reaches us in one of two shapes: an HttpErrorResponse with
   * status 0, or (because most services reject with `error.error` rather than the
   * response) the raw DOM ProgressEvent that HttpClient puts inside it. Both are
   * the same event to the user, so both produce this sentence.
   *
   * Without this, the ProgressEvent fell through every branch of
   * extractErrorMessage to the last-resort JSON.stringify, whose only enumerable
   * property is isTrusted - which is how '{"isTrusted":true}' ended up in an
   * alert box on the declarations entry list.
   */
  private static readonly REQUEST_DID_NOT_COMPLETE: string =
    "The request didn't complete - the server may be taking too long to respond, or the connection dropped. Please try again.";

  private static readonly REQUEST_TIMED_OUT: string =
    'The server took too long to respond. Please try again.';

    public messages: Array<IAlert> = [];
    message_count: number = 0;


  public closeAlert(alert: IAlert) {
    const index: number = this.alerts.indexOf(alert);
    this.alerts.splice(index, 1);
  }

  protected handleError(error: any): void {
    console.log('Error details:', error);

    this.error_count++;

    const errorMessage = this.extractErrorMessage(error);

    this.alerts.push({
      id: this.error_count,
      type: 'danger',
      message: errorMessage
    });

    // Scroll to alerts with null check
    if (this.myScrollContainer?.nativeElement) {
      this.myScrollContainer.nativeElement.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  }

  private extractErrorMessage(error: any): string {
    // Handle null/undefined
    if (!error) {
      return 'An unknown error occurred';
    }

    // Handle HttpErrorResponse
    if (error instanceof HttpErrorResponse) {
      // Check for custom error message in response body
      if (error.error?.message) {
        return error.error.message;
      }

      // Check for error array (common in validation responses)
      if (error.error?.errors && Array.isArray(error.error.errors)) {
        return error.error.errors.join(', ');
      }

      // Check for simple error object
      if (error.error && typeof error.error === 'string') {
        return error.error;
      }

      // Fall back to status-based messages
      switch (error.status) {
        case 0:
          return SmsAbstractAppPage.REQUEST_DID_NOT_COMPLETE;
        case 400:
          return 'Invalid request - please check your input';
        case 401:
          return 'You are not authorized to perform this action';
        case 403:
          return 'Access denied';
        case 404:
          return 'The requested resource was not found';
        case 408:
          return SmsAbstractAppPage.REQUEST_TIMED_OUT;
        case 409:
          return 'Conflict - this action cannot be completed';
        case 422:
          return 'Validation error - please check your input';
        case 500:
          return 'Server error - please try again later';
        case 503:
          return 'Service temporarily unavailable';
        case 504:
          return SmsAbstractAppPage.REQUEST_TIMED_OUT;
        default:
          return `Server error (${error.status}): ${error.statusText || 'Unknown error'}`;
      }
    }

    // Handle Error objects
    if (error instanceof Error) {
      return error.message || 'An unexpected error occurred';
    }

    // Handle a bare DOM event - a ProgressEvent from a request that never
    // completed, unwrapped out of its HttpErrorResponse by the calling service.
    // It carries no message of its own, so anything below would stringify it.
    if (typeof Event !== 'undefined' && error instanceof Event) {
      return SmsAbstractAppPage.REQUEST_DID_NOT_COMPLETE;
    }

    // Handle string errors
    if (typeof error === 'string') {
      return error;
    }

    // Handle objects with message property
    if (error && typeof error === 'object') {
      if (error.message) {
        return error.message;
      }

      if (error.error && typeof error.error === 'string') {
        return error.error;
      }

      if (error.title) {
        return error.title;
      }
    }

    // Last resort - try to stringify safely
    try {
      const stringified = JSON.stringify(error);
      return stringified !== '{}' ? stringified : 'An unknown error occurred';
    } catch {
      return 'An unknown error occurred';
    }
  }

  public closeMessage(message: IAlert) {
    const index: number = this.messages.indexOf(message);
    this.messages.splice(index, 1);
  }

  protected handleMessage(message: any, message_type: string){

    this.message_count ++;
      this.messages.push({
        id: this.message_count,
        type: message_type,
        message: message
      });

  }

}
